import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ProgressBar from '~valuation/pages/AddValuation/ProgressBar';
import EvaluationGoalStep from '~valuation/pages/AddValuation/EvaluationGoalStep';
import LocationStep from '~valuation/pages/AddValuation/LocationStep';
import PropertyPriceStep from '~valuation/pages/AddValuation/PropertyPriceStep';
import EstimatedPriceEntryStep from '~valuation/pages/AddValuation/EstimatedPriceEntryStep';
import PropertyDataAnalysisStep from '~valuation/pages/AddValuation/PropertyDataAnalysisStep';
import { EvaluationSteps } from '~valuation/enums';
import { ValuationTypeEnum } from '~/constants/enums/valuationEnums';
import PropertyDetailsStep from '~valuation/pages/AddValuation/PropertyDetailsStep';
import {
  addFormPortfolioSchema,
  addFormEstimationSchema,
} from '~valuation/utils/addFormSchema';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint, useFullRoutes } from '~/hooks';
import { FormType } from '~valuation/utils/addFormSchema';
import ValuationBanner from '~valuation/components/ValuationBanner';
import {
  useAddPropertyValuationMutation,
  useGetValuationFormSchemaMutation,
} from '~/redux/api/valuationApiSlice';

const MIN_LOADING_DELAY_MS = 3000;
const VALUATION_BANNER_IMAGE =
  'https://img-0.aqarmap.com.eg/aqarmap/hero/images/homepage/hero-6.jpg';

const AddValuation = () => {
  const [activeStep, setActiveStep] = React.useState(EvaluationSteps.GOAL);
  const [valuationType, setValuationType] = useState('');
  const [getValuationFormSchema, { data, isError }] =
    useGetValuationFormSchemaMutation();
  const navigate = useNavigate();
  const [addValuation] = useAddPropertyValuationMutation();
  const { VALUATION_URL } = useFullRoutes();
  const { mobileScreen } = useBreakpoint();

  const methods = useForm<FormType>({
    resolver:
      valuationType === ''
        ? undefined
        : zodResolver(
            valuationType === ValuationTypeEnum.PORTFOLIO
              ? addFormPortfolioSchema
              : addFormEstimationSchema,
          ),
    mode: 'onTouched',
  });

  const onSubmit = async (data: FormType) => {
    if (valuationType === ValuationTypeEnum.PORTFOLIO) {
      const start = Date.now();

      const res = await addValuation(data);

      const elapsed = Date.now() - start;
      const minDelay = MIN_LOADING_DELAY_MS;

      if (elapsed < minDelay)
        await new Promise((resolve) => setTimeout(resolve, minDelay - elapsed));

      if ('data' in res) navigate(VALUATION_URL.RESULTS(res.data.valuation.id));
    } else if (valuationType === ValuationTypeEnum.ESTIMATION) {
      const res = await addValuation(data);
      if ('data' in res) navigate(VALUATION_URL.RESULTS(res.data.valuation.id));
    }
  };

  useEffect(() => {
    const fetchFormSchema = async () => {
      if (valuationType && activeStep === EvaluationSteps.LOCATION) {
        try {
          await getValuationFormSchema(valuationType).unwrap();
        } catch (error) {
          console.error('Error fetching form schema:', error);
        }
      }
    };

    fetchFormSchema();
  }, [valuationType, getValuationFormSchema, activeStep]);

  useEffect(() => {
    if (activeStep === EvaluationSteps.DATA_ANALYSIS)
      methods.handleSubmit(onSubmit)();
  }, [activeStep]);

  useEffect(() => {
    if (isError) setActiveStep(EvaluationSteps.GOAL);
  }, [isError]);

  useEffect(() => {
    setValuationType(methods.watch('type'));
  }, [methods.watch('type')]);

  useEffect(() => {
    if (activeStep !== EvaluationSteps.GOAL)
      window.scrollTo({ top: mobileScreen ? 0 : 200, behavior: 'smooth' });
  }, [activeStep]);

  return (
    <div className="flex flex-col gap-5x items-center justify-center w-full pb-8x md:px-0 pt-2x md:pt-0 md:bg-[#F5F5F566]">
      <ValuationBanner
        hideOnMobile
        image={
          <div className=" flex items-center justify-center w-full">
            <img
              src={VALUATION_BANNER_IMAGE}
              alt="Valuation"
              className=" h-[288px] w-[563px] rounded-[16px] "
            />
          </div>
        }
      />
      <div className="max-w-[726px] w-full flex flex-col gap-4x md:gap-5x">
        <ProgressBar activeStep={activeStep} valuationType={valuationType} />
        <FormProvider {...methods}>
          <div className="md:shadow-[0px_4px_12px_0px_#00000014] rounded-[12px] md:px-5x md:pt-4x md:pb-7x bg-white">
            {activeStep === EvaluationSteps.GOAL && (
              <EvaluationGoalStep setActiveStep={setActiveStep} />
            )}
            {activeStep === EvaluationSteps.LOCATION && (
              <LocationStep setActiveStep={setActiveStep} />
            )}
            {activeStep === EvaluationSteps.PROPERTY_DETAILS && (
              <PropertyDetailsStep
                setActiveStep={setActiveStep}
                formSchemaData={data}
              />
            )}
            {valuationType === ValuationTypeEnum.PORTFOLIO && (
              <>
                {activeStep === EvaluationSteps.PROPERTY_PRICE && (
                  <PropertyPriceStep setActiveStep={setActiveStep} />
                )}
                {activeStep === EvaluationSteps.USER_ESTIMATED_PRICE && (
                  <EstimatedPriceEntryStep setActiveStep={setActiveStep} />
                )}
              </>
            )}
            {activeStep === EvaluationSteps.DATA_ANALYSIS && (
              <PropertyDataAnalysisStep />
            )}
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddValuation;
